<template>
  <section
    class="marketing-object-details position-relative"
    data-cy="marketing-object-details-view"
    :data-view-ready="!!item && !loading">
    <div
      v-if="item === null"
      class="col-12 text-center">
      <coozzy-spinner class="m-3" />
    </div>
    <template v-else>
      <div class="row">
        <div class="col-12">
          <coozzy-page-title
            :title="$t('message.onBoarding.buildings.title') + ': ' + buildingName"
            :sub-title="$t('message.onBoarding.buildings.address') + ': ' + item.address.street + ' ' + item.address.streetNumber + ', ' +item.address.zip + ' ' + item.address.city" />
          <h5>
            <span>{{ item.publicId }}</span>
            <span v-if="item.name"> | {{ item.name | displayOptionalValue }}</span>
            <span>
              | {{ $t('message.marketingObjects.status.title') }}:
              <template v-if="!isRented">
                <span
                  data-cy="marketing-object-details-status"
                  :class="statusColor">{{ statusTitle }}</span>
                <span
                  v-if="statusFutureDate"
                  :class="statusColor">
                  {{ $t('message.generic.until') + ' ' + statusFutureDate }}
                </span>
                <span
                  v-else-if="statusDate"
                  :class="statusColor">
                  {{ $t('message.generic.since') + ' ' + statusDate }}
                </span>
                <span
                  v-if="loadedReservedRequest"
                  :class="statusColor">
                  - {{ $t('message.manageInterestedParties.reserved') }}
                </span>
              </template>
              <template v-else>
                <span
                  class="text-blue">
                  {{ $t('message.marketingObjects.status.rented') }}
                </span>
                <span
                  v-if="futureTenancy"
                  class="text-blue"> {{ $t('message.generic.fromDate') }}{{ futureTenancy.startingAt | objectToDate }}</span>
              </template>
            </span>
            <coozzy-alert
              v-if="item.marketingStatus && (item.marketingStatus.startMarketingAt || item.marketingStatus.marketingNote)"
              class="my-2"
              variant="blue"
              size="sm">
              <template v-if="!updateMarketingInfoEdit">
                <div>
                  <h6 class="d-inline-block alert-heading font-weight-bold mb-0">
                    {{ $t('message.marketingObjects.startMarketing') }}:
                  </h6>
                  {{ startMarketingAt | timestampToDate }}
                  <div
                    class="d-inline-flex float-right"
                    @mousedown="openEditNote">
                    <coozzy-button
                      data-cy="marketing-object-details-edit-note-button"
                      class="mr-3 mb-3">
                      <coozzy-edit-icon class="too-left" />
                      <span
                        class="ml-1">
                        {{ $t('message.marketingObjects.editNote') }}
                      </span>
                    </coozzy-button>
                  </div>
                </div>
                <h6 class="alert-heading font-weight-bold mb-0 mt-2">
                  {{ $t('message.marketingObjects.marketingNote') }}:
                </h6>
                <div
                  v-if="marketingNote.length < 60 ">
                  <!-- eslint-disable vue/no-v-html -->
                  <span
                    style="white-space: pre;"
                    v-html="marketingNote" />
                  <!-- eslint-enable vue/no-v-html -->
                </div>
                <div
                  v-else-if="!marketingNoteVisibility">
                  <!-- eslint-disable vue/no-v-html -->
                  <span
                    style="white-space: pre;"
                    v-html="marketingNote.substring(0, 20)" />...
                  <!-- eslint-enable vue/no-v-html -->
                  <b-link
                    @click="showMoreShowLess">
                    {{ $t('message.generic.showMore') }}
                  </b-link>
                </div>
                <div
                  v-else>
                  <b-collapse
                    id="collapse-1"
                    v-model="marketingNoteVisibility">
                    <!-- eslint-disable vue/no-v-html -->
                    <span
                      style="white-space: pre;"
                      v-html="marketingNote" />
                  <!-- eslint-enable vue/no-v-html -->
                  </b-collapse>
                  <b-link
                    @click="showMoreShowLess">
                    {{ $t('message.generic.showLess') }}
                  </b-link>
                </div>
              </template>
              <template v-else>
                <div class="row py-2">
                  <div class="col-sm-12 col-md-4">
                    <coozzy-form-input
                      v-model="startMarketing"
                      type="date"
                      :filter="'formatDate'"
                      :name="$t('message.marketingObjects.startMarketing')"
                      @blur="dateToTimestamp()" />
                  </div>
                  <div class="col">
                    <coozzy-button
                      design="primary"
                      class="float-right"
                      @click="updateMarketingInfo">
                      <coozzy-save-icon />
                      <span
                        class="ml-1">
                        {{ $t('message.generic.form.save') }}
                      </span>
                    </coozzy-button>
                  </div>
                </div>
                <div class="row py-2">
                  <div class="col-sm-12 col-md-6">
                    <coozzy-form-textarea
                      ref="description"
                      v-model="marketingNote"
                      class="marketingNote"
                      :initial="marketingNote"
                      :value-text-area="marketingNote"
                      :row="5"
                      name="marketingNote"
                      :placeholder="$t('message.marketingObjects.marketingNote')">
                      {{ $t('message.marketingObjects.marketingNote') }}
                    </coozzy-form-textarea>
                  </div>
                </div>
              </template>
            </coozzy-alert>
          </h5>
        </div>
      </div>
      <!-- Action buttons on mobile -->
      <div class="row d-md-none">
        <div class="col-12 mb-2">
          <coozzy-button
            data-cy="marketing-object-details-new-request-button"
            design="prop-green"
            class="w-100"
            @click="$bvModal.show('create-contact-modal-marketing-details')">
            {{ $t('message.generic.newRequest') }}
          </coozzy-button>
        </div>
        <div class="col-12 mb-2">
          <coozzy-button
            data-cy="marketing-object-details-edit-advert-button"
            design="primary"
            class="w-100"
            @click="editClicked"
            @click.middle="editClickedMiddle">
            {{ $t('message.generic.editAdvertActionButton') }}
          </coozzy-button>
        </div>
        <div
          v-if="canPromoteAdvert"
          class="col-12 mb-2">
          <coozzy-button
            design="prop-green"
            class="w-100"
            @click="promoteAdvertClicked">
            {{ $t('message.marketingObjects.promoteAdvert') }}
          </coozzy-button>
        </div>
        <div class="col-12 mb-2">
          <coozzy-button
            data-cy="marketing-object-details-open-expose-button"
            design="primary"
            class="w-100"
            @click="$bvModal.show('openExposeModal')">
            {{ $t('message.advertisement.action.openExpose') }}
          </coozzy-button>
        </div>
        <div class="col-12 mb-2">
          <coozzy-button
            v-if="checkOnline || checkOnlineUntil !== null"
            id="unpublish-btn1"
            v-b-tooltip.hover="unpublishTooltipConfig"
            data-cy="marketing-object-details-unpublish-button"
            design="red"
            class="w-100"
            @click="$bvModal.show('confirmationModal')">
            {{ $t('message.marketingObjects.unPublish') }}
          </coozzy-button>
          <coozzy-button
            v-else
            id="publish-btn1"
            v-b-tooltip.hover="publishTooltipConfig"
            data-cy="marketing-object-details-publish-button"
            :design="canBePublished ? 'red' : 'deactivated'"
            :disabled="isRented"
            class="w-100"
            @click="publishClicked">
            {{ $t('message.marketingObjects.publish') }}
          </coozzy-button>
        </div>
      </div>
      <div class="row">
        <div class="col-12 mb-3 customTabs">
          <b-tabs
            v-model="tabIndex"
            nav-class="marketing-details-nav">
            <b-tab
              class=""
              :title="$t('message.marketingObjects.details.tabs.interestedParties')">
              <template #title>
                <router-link
                  data-cy="marketing-object-details-tab-requests"
                  :to="getMarketingRoute(0)">
                  <span>{{ $t('message.marketingObjects.details.tabs.interestedParties') }}</span>
                </router-link>
              </template>
              <interested-parties-tab
                v-if="!requestSelected || elementSelected === ''"
                :key="id"
                ref="interested-tab"
                :object="item"
                :is-rented="isRented"
                :request-selected="elementSelected"
                :filter="propsFilter"
                :filter-progress="propsFilterProgress"
                :filter-market-place-name="propsFilterMarketPlaceName"
                :link-mail="linkMail"
                :link-email="linkEmail"
                @loaded-reserved-request="onLoadedReservedRequest()"
                @loaded-request="onLoadedRequest"
                @set-request-selected="setRequestSelected" />
              <interested-party-details
                v-else
                ref="interested-detail"
                :key="requestSelected.id"
                :request="requestSelected"
                :list-request="listRequest"
                :is-tenancy-activated="isTenancyActivated"
                :object="item"
                :request-building="building"
                :is-reserved="loadedReservedRequest"
                @delete-tenancy="deleteTenancy"
                @tenancy-activated="tenancyActivated"
                @request-updated="setRequestSelected" />
            </b-tab>
            <b-tab
              :title="$t('message.marketingObjects.details.tabs.advert')"
              class="p-0">
              <template #title>
                <router-link
                  data-cy="marketing-object-details-tab-advert"
                  :to="getMarketingRoute(1)">
                  <span>{{ $t('message.marketingObjects.details.tabs.advert') }}</span>
                </router-link>
              </template>
              <marketing-object-advert-tab
                :object-selected="item"
                :read-only="true"
                :property="property" />
            </b-tab>
            <b-tab
              lazy
              :title="$t('message.marketingObjects.details.tabs.contacts')">
              <template #title>
                <router-link
                  data-cy="marketing-object-details-tab-contacts"
                  :to="getMarketingRoute(2)">
                  <span>{{ $t('message.marketingObjects.details.tabs.contacts') }}</span>
                </router-link>
              </template>
              <create-contacts-tab
                :read-only="true"
                :show-add-buttons="false"
                :item="item" />
            </b-tab>
            <b-tab
              lazy
              :title="$t('message.marketingObjects.details.tabs.viewingAppointments')">
              <template #title>
                <router-link
                  data-cy="marketing-object-details-tab-viewing-appointments"
                  :to="getMarketingRoute(3)">
                  <span>{{ $t('message.marketingObjects.details.tabs.viewingAppointments') }}</span>
                </router-link>
              </template>
              <viewing-appointments-tab
                v-if="item"
                :object="item"
                :building="building"
                :building-ready="buildingReady"
                @object-updated="onViewingAppointmentsObjectUpdated"
                @open-request="openRequestFromViewingAppointments" />
            </b-tab>
            <template
              #tabs-end>
              <li class="nav-item item-button marketing-details-actions d-none d-md-flex">
                <coozzy-button
                  v-if="canPromoteAdvert"
                  design="prop-green"
                  @click.prevent="promoteAdvertClicked">
                  {{ $t('message.marketingObjects.promoteAdvert') }}
                </coozzy-button>
                <coozzy-button
                  data-cy="marketing-object-details-new-request-button"
                  design="prop-green"
                  @click.prevent="$bvModal.show('create-contact-modal-marketing-details')">
                  {{ $t('message.generic.newRequest') }}
                </coozzy-button>
                <coozzy-button
                  data-cy="marketing-object-details-edit-advert-button"
                  design="primary"
                  @click.prevent="editClicked"
                  @click.middle="editClickedMiddle">
                  {{ $t('message.generic.editAdvertActionButton') }}
                </coozzy-button>
                <coozzy-button
                  data-cy="marketing-object-details-open-expose-button"
                  design="primary"
                  @click.prevent="$bvModal.show('openExposeModal')">
                  {{ $t('message.advertisement.action.openExpose') }}
                </coozzy-button>
                <coozzy-button
                  v-if="checkOnline || checkOnlineUntil !== null"
                  id="unpublish-btn"
                  v-b-tooltip.hover="unpublishTooltipConfig"
                  data-cy="marketing-object-details-unpublish-button"
                  design="red"
                  class="m-0"
                  @click="$bvModal.show('confirmationModal')">
                  {{ $t('message.marketingObjects.unPublish') }}
                </coozzy-button>
                <coozzy-button
                  v-else
                  id="publish-btn2"
                  v-b-tooltip.hover="desktopPublishTooltipConfig"
                  data-cy="marketing-object-details-publish-button"
                  :design="canBePublished ? 'red' : 'deactivated'"
                  :disabled="isRented"
                  @click="publishClicked">
                  {{ $t('message.marketingObjects.publish') }}
                </coozzy-button>
              </li>
            </template>
          </b-tabs>
        </div>
      </div>
    </template>
    <div
      v-if="loading && item"
      class="marketing-details-loading-overlay">
      <coozzy-spinner class="m-3" />
    </div>
    <create-contact-modal
      v-if="item"
      :key="item.id"
      modal-id="create-contact-modal-marketing-details"
      :for-owner-id="parseInt(item.ownerId)"
      :show-search="true"
      :show-request-sources="true"
      :show-roles-input="false"
      :set-interested-party-role="true"
      :show-add-another="false"
      :enable-delete="false"
      @contact-created="contactCreated"
      @contact-updated="contactUpdated" />
    <b-modal
      :id="'confirmationModal'"
      ref="confirmationModal"
      data-cy="marketing-object-details-unpublish-modal"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.marketingObjects.confirmationUnPublish.title')">
      <template>
        <div class="col">
          <p>{{ $t('message.marketingObjects.confirmationUnPublish.body') }}</p>
        </div>
        <div class="col">
          <coozzy-button
            data-cy="marketing-object-details-unpublish-cancel-button"
            size="small"
            class="mb-0 border"
            design="transparent"
            @click="$bvModal.hide('confirmationModal')">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
          <coozzy-button
            data-cy="marketing-object-details-unpublish-confirm-button"
            size="small"
            class="float-right mb-0"
            design="green"
            @click="unPublishClicked">
            {{ $t('message.generic.confirm') }}
          </coozzy-button>
        </div>
      </template>
    </b-modal>
    <b-modal
      :id="'openExposeModal'"
      ref="openExposeModal"
      data-cy="marketing-object-details-open-expose-modal"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.advertisement.action.openExpose')">
      <template>
        <div class="col-12">
          <p>{{ $t('message.advertisement.action.titleModal') }}</p>
        </div>
        <div class="col-12 mt-3 mb-3">
          <coozzy-form-select
            v-model="language"
            data-cy="marketing-object-details-open-expose-language-select">
            <option
              v-for="option in options"
              :key="option.key"
              :value="option.key">
              {{ option.value }}
            </option>
          </coozzy-form-select>
        </div>
        <div class="col">
          <coozzy-button
            data-cy="marketing-object-details-open-expose-cancel-button"
            size="small"
            class="mb-0 border"
            design="transparent"
            @click="$bvModal.hide('openExposeModal')">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
          <coozzy-button
            data-cy="marketing-object-details-open-expose-confirm-button"
            size="small"
            class="float-right mb-0"
            design="green"
            @click="openAdvertExpose">
            {{ $t('message.generic.confirm') }}
          </coozzy-button>
        </div>
      </template>
    </b-modal>
  </section>
</template>

<script>
import ObjectApi from '@/misc/apis/ObjectApi'
import MessageApi from '@/misc/apis/MessageApi'
import ExportApi from '@/misc/apis/ExportApi'
import CoozzySpinner from '../framework/components/misc/CoozzySpinner'
import MarketingObjectAdvertTab from './MarketingObjectAdvertTab'
import InterestedPartiesTab from './InterestedPartiesTab'
import InterestedPartyDetails from './InterestedPartyDetails'
import CoozzyButton from '../framework/components/button/CoozzyButton'
import Vue from 'vue'
import { formatting } from '@/mixins/formatting'
import { obj } from '@/mixins/object'
import CoozzyAlert from '@/framework/components/alert/CoozzyAlert'
import RequestApi from '@/marketingBasic/api/RequestApi'
import CreateContactsTab from '@/building/tabs/CreateContactsTab'
import ViewingAppointmentsTab from '@/marketing/ViewingAppointmentsTab'
import CoozzyPageTitle from '@/framework/layout/CoozzyPageTitle'
import CreateContactModal from '@/contact/components/CreateContactModal'
import { dateUtils } from '@/mixins/dateUtils'
import CoozzyEditIcon from '@/framework/components/icons/CoozzyEditIcon'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import CoozzySaveIcon from '@/framework/components/icons/CoozzySaveIcon.vue'
import CoozzyFormTextarea from '@/framework/components/form/textarea/CoozzyFormTextarea.vue'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect.vue'

export default {
  name: 'MarketingObjectDetailsView',
  components: {
    CoozzyFormSelect,
    CoozzyFormTextarea,
    CoozzySaveIcon,
    CoozzyFormInput,
    CreateContactModal,
    CoozzyPageTitle,
    CreateContactsTab,
    ViewingAppointmentsTab,
    CoozzyAlert,
    InterestedPartiesTab,
    InterestedPartyDetails,
    CoozzyButton,
    MarketingObjectAdvertTab,
    CoozzySpinner,
    CoozzyEditIcon
  },
  mixins: [formatting, obj, dateUtils],
  beforeRouteUpdate(to, from, next) {
    next()

    if (to.name !== from.name) {
      return
    }

    const objectChanged = to.params.id !== from.params.id
    const requestId = to.query.elementSelected || ''
    const view = parseInt(to.query.view || 0, 10)

    if (objectChanged) {
      this.requestSelected = null
      this.tabIndex = Number.isNaN(view) ? 0 : view
      this.loadObject(to.params.id)
      return
    }

    if (requestId && (!this.requestSelected || this.requestSelected.id !== requestId)) {
      this.isNavigatingToRequest = true
      this.tabIndex = 0
      this.openRequestById(requestId)
    }
  },
  props: {
    elementSelected: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: ''
    },
    view: {
      type: [String, Number],
      default: 0
    },
    filter: {
      type: String,
      default: ''
    },
    filterProgress: {
      type: String,
      default: ''
    },
    filterMarketPlaceName: {
      type: String,
      default: ''
    },
    isTenancyActivated: {
      type: Boolean,
      default: false
    },
    linkMail: {
      type: String,
      default: ''
    },
    linkEmail: {
      type: String,
      default: ''
    }
  },
  metaInfo() {
    return {
      title: `${this.$t('message.navigation.marketing.title')} ${this.buildingName}`
    }
  },
  data() {
    return {
      marketingNote: '',
      startMarketing: '',
      startMarketingAt: '',
      tabIndex: 0,
      loading: false,
      item: null,
      futureTenancy: null,
      loadedReservedRequest: false,
      isRented: false,
      isNavigatingToRequest: false,
      requestSelected: null,
      requestNoReject: null,
      hasPlatforms: false,
      hasNewHomeExport: false,
      marketingNoteVisibility: false,
      updateMarketingInfoEdit: false,
      listRequest: [],
      rejectOtherRequest: false,
      building: null,
      buildingReady: false,
      property: null,
      language: 'DE'
    }
  },
  computed: {
    options() {
      return [
        { key: 'EN', value: this.$t('message.language.english') },
        { key: 'DE', value: this.$t('message.language.german') },
        { key: 'IT', value: this.$t('message.language.italian') }]
    },
    getUserLang() {
      return this.$store.getters['user/getCurrentUser']['https://coozzy.ch/lang'].toUpperCase()
    },
    checkOnline() {
      if (this.item?.marketingStatus?.publications.length > 0) {
        const onlinePublications = this.item.marketingStatus.publications.filter(element => this.isPublicationCurrentlyOnline(element))
        if (this.item.marketingStatus.publications.find(element => element.unPublishedByAccountId <= 0) && onlinePublications.length > 0) {
          return true
        }
      }
      return false
    },
    checkOnlineUntil() {
      if (this.item?.marketingStatus?.publications.length > 0) {
        const onlinePublications = this.item.marketingStatus.publications.filter(element => this.isPublicationCurrentlyOnline(element))
        if (this.statusTitle !== 'Online' || onlinePublications.length === 0) {
          return null
        }
        const publication = onlinePublications[onlinePublications.length - 1]
        if (publication && publication.unPublished > 0 && parseInt(publication.unPublished) * 1000 > Date.now()) {
          return publication.unPublished
        }
        if (publication && publication.unPublishAtAmountOfRequests > 0) {
          return publication.unPublishAtAmountOfRequests
        }
        return null
      }
      return null
    },
    statusTitle() {
      return this.calculateMarketingStatus(this.item.marketingStatus).label
    },
    statusColor() {
      return this.calculateMarketingStatus(this.item.marketingStatus).color
    },
    statusDate() {
      return this.calculateMarketingStatus(this.item.marketingStatus).date
    },
    statusFutureDate() {
      return this.calculateMarketingStatus(this.item.marketingStatus).dateFuture
    },
    canBePublished() {
      return !!(this.item && this.item.title !== '' && this.item.description !== '' && this.item.category !== 'UNDEFINED_CATEGORY' &&
        this.item.pricing.currency !== '' && (this.item.pricing.coldRent !== '' || this.item.pricing.unit !== 'ON_REQUEST') && this.hasPlatforms && this.statusTitle !== 'Online')
    },
    canPromoteAdvert() {
      return (this.checkOnline || this.checkOnlineUntil !== null) && this.hasNewHomeExport
    },
    unpublishTooltipConfig() {
      if (this.checkOnlineUntil === null) {
        return false
      }
      const until = parseInt(this.checkOnlineUntil)
      const isFutureDate = (new Date(until)).getTime() > (new Date(Date.now() - 86400000)).getTime() / 1000
      return {
        title: isFutureDate
          ? this.$t('message.marketingObjects.objectAutomaticallyUnpublishDate', {
            datetime: this.$options.filters.timestampToDateTime(until)
          })
          : this.$t('message.marketingObjects.objectAutomaticallyUnpublishAmount', {
            amount: this.checkOnlineUntil
          })
      }
    },
    publishTooltipConfig() {
      if (this.canBePublished) {
        return false
      }
      return {
        title: this.$t('message.marketingObjects.impossibleToPublish')
      }
    },
    desktopPublishTooltipConfig() {
      if (this.canBePublished || this.statusTitle === 'Online') {
        return false
      }
      return {
        title: this.$t('message.marketingObjects.impossibleToPublish')
      }
    },
    buildingName() {
      if (this.building) {
        return this.building.name
      } else {
        return '-'
      }
    },
    propsFilter() {
      return this.filter
    },
    propsFilterProgress() {
      return this.filterProgress
    },
    propsFilterMarketPlaceName() {
      return this.filterMarketPlaceName
    }
  },
  watch: {
    tabIndex: function (newVal) {
      if (!this.isNavigatingToRequest) {
        this.requestSelected = null
      }
      this.isNavigatingToRequest = false

      if (!this.$route.query || parseInt(this.$route.query.view || 0) !== newVal) {
        // Use $route.query.elementSelected directly to preserve the current URL value
        const currentElementSelected = this.$route.query.elementSelected || ''
        this.$router.replace({
          name: 'MarketingObjectDetailsView',
          params: { id: this.id },
          query: {
            view: newVal,
            elementSelected: newVal === 0 ? currentElementSelected : ''
          }
        })
      }
    }
  },
  mounted() {
    this.$nextTick(function () {
      this.language = this.getUserLang || 'DE'
    })
    this.openSelectedTabs()
    this.loadObject()
  },
  methods: {
    updateMarketingInfo() {
      ObjectApi.updateMarketingInfo(this.item.id, this.marketingNote, this.startMarketingAt)
        .then(() => {
          this.updateMarketingInfoEdit = false
          Vue.toasted.show(this.$t('message.successMessages.marketingNoteAdded'), { type: 'success' })
        })
        .catch(e => {
          console.log(e)
        })
    },
    dateToTimestamp() {
      this.startMarketingAt = this.toTimestamp(this.startMarketing)
    },
    toTimestamp(strDate) {
      return Date.parse(strDate) / 1000
    },
    loadObject(objectId = this.id) {
      this.isRented = false
      this.loadedReservedRequest = false
      this.futureTenancy = null
      ObjectApi.getObject(objectId)
        .then(response => {
          this.item = response.object
          this.startMarketingAt = this.item.marketingStatus.startMarketingAt
          this.startMarketing = this.$options.filters.timestampToDateInput(this.startMarketingAt)
          this.marketingNote = this.item.marketingStatus.marketingNote
          this.loadBuilding(this.item.buildingId)
          this.loadProperty(this.item.buildingId)
          this.loadExportConfigurations()
          ExportApi.getExportPlatforms(this.item.id)
            .then(response => {
              this.hasPlatforms = response.platforms.length > 0
            })
            .catch(e => {
              console.log(e)
              Vue.toasted.show(this.$t('message.loadingErrors.exportPlatforms'), { type: 'error' })
            })
          this.loadCurrentStatus()
          this.loadRequests()
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.object'), { type: 'error' })
        })
    },
    loadBuilding(buildingId) {
      this.buildingReady = false
      if (!buildingId) {
        this.buildingReady = true
        return
      }
      ObjectApi.getBuilding(buildingId)
        .then(response => {
          this.building = response.building
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.object'), { type: 'error' })
        })
        .finally(() => {
          this.buildingReady = true
        })
    },
    loadProperty(buildingId) {
      ObjectApi.getPropertiesByBuildingIds([buildingId])
        .then(response => {
          this.property = response.properties[0]
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.object'), { type: 'error' })
        })
    },
    loadExportConfigurations() {
      ExportApi.getExports(this.$store.getters['user/getAccountId'])
        .then(response => {
          this.hasNewHomeExport = response.exports.some(e => e.platform === 'NEWHOME_PLATFORM')
        })
        .catch(e => {
          console.log(e)
          this.hasNewHomeExport = false
          Vue.toasted.show(this.$t('message.loadingErrors.exportPlatforms'), { type: 'error' })
        })
    },
    loadCurrentStatus() {
      if (this.item.marketingStatus && this.item.marketingStatus.clearFromMarketingAt > 0) {
        this.$nextTick(function () {
          this.isRented = true
        })
      }
    },
    deleteTenancy() {
      this.loadRequests()
    },
    openSelectedTabs() {
      this.tabIndex = parseInt(this.view)
    },
    editClicked() {
      this.$router.push({
        name: 'MarketingEditAdvertView',
        params: { id: this.id }
      })
    },
    promoteAdvertClicked() {
      localStorage.setItem('oldCurrentStep', '3')
      this.editClicked()
    },
    editClickedMiddle() {
      const routeData = this.$router.resolve({
        name: 'MarketingEditAdvertView',
        params: { id: this.id }
      })
      window.open(routeData.href, '_blank')
    },
    async openAdvertExpose() {
      this.loading = true
      try {
        this.$bvModal.hide('openExposeModal')
        const blob = new Blob([await ObjectApi.getMarketingExpose(this.item.id, this.language)], { type: 'application/pdf' })
        const blobURL = URL.createObjectURL(blob)
        window.open(blobURL)
      } finally {
        this.loading = false
      }
    },
    /**
     * Publication is currently online when published is in the past and
     * unPublished is unset/0 or still in the future (Online until = date).
     */
    isPublicationCurrentlyOnline(element) {
      const publishedSec = parseInt(element.published)
      const unPublishedSec = parseInt(element.unPublished)
      const nowMs = Date.now()
      if (!(publishedSec > 0) || publishedSec * 1000 > nowMs) {
        return false
      }
      return !(unPublishedSec > 0) || unPublishedSec * 1000 > nowMs
    },
    unPublishClicked() {
      this.loading = true
      ObjectApi.unPublishObject([this.id])
        .then(() => {
          this.loadObject()
          this.loadRequests()
          this.$bvModal.hide('requestDeactivateAdvertModal')
          this.$bvModal.hide('confirmationModal')
          Vue.toasted.show(this.$t('message.successMessages.unPublishObject'), { type: 'success' })
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.unPublishObject'), { type: 'error' })
        })
        .finally(() => {
          this.loading = false
        })
    },
    publishClicked() {
      if (this.canBePublished) {
        this.loading = true
        const scheduled = this.getScheduledPublicationParams(this.item?.marketingStatus)
        ObjectApi.publishObject(
          [this.id],
          scheduled.unPublishAt,
          scheduled.unPublishAtAmountOfRequests,
          scheduled.specialPublication,
          scheduled.publishAt
        )
          .then(() => {
            this.loadObject()
            this.loadRequests()
            Vue.toasted.show(this.$t('message.successMessages.publishObject'), { type: 'success' })
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.savingErrors.publishObject'), { type: 'error' })
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    contactCreated(data) {
      this.createNewRequest(data.contact, data.requestData.source, data.requestData.marketplaceName)
    },
    contactUpdated(data) {
      this.createNewRequest(data.contact, data.requestData.source, data.requestData.marketplaceName)
    },
    createNewRequest(contact, source, marketplaceName) {
      // Check if we already got a request on this object with this contact
      for (const request of this.listRequest) {
        if (request.requesters.map(req => req.contactId).includes(contact.id)) {
          Vue.toasted.show(this.$t('message.savingErrors.contactAlreadyApplied'), { type: 'error' })
          this.setRequestSelected(request)
          return
        }
      }

      const request = {
        ownerId: this.item.ownerId,
        source: source,
        marketplaceName: marketplaceName,
        objectId: this.item.id,
        requesters: [{
          accountId: contact.accountId,
          userId: contact.userReference,
          contactId: contact.id
        }],
        checklist: null,
        createdByReceiver: false,
        favorite: false,
        initialMessage: ''
      }
      RequestApi.newRequest(request)
        .then(response => {
          Vue.toasted.show(this.$t('message.successMessages.requestAdded'), { type: 'success' })
          this.listRequest.push(response.request)
          if (this.linkEmail !== '') {
            this.loading = true
            const toLink = response.request.requesters[0].contactId
            MessageApi.setReferenceIds([this.linkMail], [toLink, response.request.id])
              .then(() => {
                Vue.toasted.show(this.$t('message.successMessages.contactLinked'), { type: 'success' })
                this.setRequestSelected(response.request)
              })
              .catch(e => {
                console.log(e)
                Vue.toasted.show(this.$t('message.savingErrors.reference'), { type: 'error' })
              })
              .finally(() => {
                this.loading = false
              })
          } else {
            this.setRequestSelected(response.request)
          }
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.request'), { type: 'error' })
        })
    },
    onLoadedReservedRequest() {
      this.loadedReservedRequest = true
    },
    isReservedRequest(request) {
      return !!request && (!!request.tenancyId || (request.tenancyIds && request.tenancyIds.length > 0))
    },
    setRequestSelected(request) {
      if (!request) {
        return
      }
      if (this.isReservedRequest(request)) {
        this.loadedReservedRequest = true
      }
      this.requestSelected = request
      if (!this.$route.query || parseInt(this.$route.query.view || 0) !== 0 || this.$route.query.elementSelected !== request.id) {
        this.$router.push({
          name: 'MarketingObjectDetailsView',
          params: { id: this.id },
          query: {
            view: 0,
            elementSelected: this.requestSelected ? this.requestSelected.id : ''
          }
        })
        this.$nextTick(function () {
          this.tabIndex = 0
        })
      }
    },
    // backAction() {
    //   if (!this.$route.query || (parseInt(this.$route.query.view || 0) === 0 && this.$route.query.elementSelected !== '')) {
    //     this.requestSelected = null
    //     this.$router.replace({
    //       name: 'MarketingObjectDetailsView',
    //       params: { id: this.id },
    //       query: {
    //         view: this.view,
    //         elementSelected: ''
    //       }
    //     })
    //   }
    // },
    getMarketingRoute(view) {
      // Never include elementSelected in tab navigation - clicking a tab should show the default view
      return { name: 'MarketingObjectDetailsView', params: { id: this.id }, query: { view: view } }
    },
    showMoreShowLess() {
      this.marketingNoteVisibility = !this.marketingNoteVisibility
    },
    tenancyActivated(requestId) {
      this.requestSelected = null
      this.requestNoReject = requestId
      this.loadObject()
      this.loadRequests()
    },
    checkReject() {
      this.$bvModal.hide('requestDeactivateAdvertModal')
      if (this.isTenancyActivated === 'true') {
        this.$refs['interested-detail'].rejectOtherRequest()
      } else {
        this.$refs['interested-tab'].rejectOtherRequest(this.requestNoReject)
      }
    },
    loadRequests() {
      this.loading = true
      RequestApi.listReceivedByObjectIds([this.item.id], null, [], [])
        .then(response => {
          this.listRequest = response.requests
          this.loadedReservedRequest = response.reserved === true || this.listRequest.some(request => this.isReservedRequest(request))
          const requestId = this.$route.query.elementSelected || ''
          if (requestId && parseInt(this.$route.query.view || 0, 10) === 0) {
            const request = this.listRequest.find(item => item.id === requestId)
            if (request) {
              this.setRequestSelected(request)
            } else if (this.$route.params.id === this.item.id) {
              this.openRequestById(requestId)
            }
          }
          for (const request of response.requests) {
            if (request.tenancyId) {
              ObjectApi.getTenancyById(request.tenancyId)
                .then(response => {
                  this.futureTenancy = response.tenancy
                })
            }
          }
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.request'), { type: 'error' })
        })
        .finally(() => {
          this.loading = false
        })
    },
    onLoadedRequest(requestList) {
      if (requestList.length > 1 && this.rejectOtherRequest) {
        this.$refs['interested-tab'].rejectOtherRequest(this.requestNoReject)
        this.loadObject()
        this.rejectOtherRequest = false
      }
    },
    openEditNote() {
      this.updateMarketingInfoEdit = !this.updateMarketingInfoEdit
    },
    onViewingAppointmentsObjectUpdated(object) {
      this.item = object
    },
    openRequestById(requestId) {
      if (!requestId) {
        return
      }
      const request = this.listRequest.find(item => item.id === requestId)
      if (request) {
        this.setRequestSelected(request)
        return
      }
      this.loading = true
      RequestApi.listByIds([requestId])
        .then(response => {
          const fetchedRequest = response.requests ? response.requests[0] : null
          if (fetchedRequest) {
            this.listRequest.push(fetchedRequest)
            this.setRequestSelected(fetchedRequest)
          }
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.request'), { type: 'error' })
        })
        .finally(() => {
          this.loading = false
        })
    },
    openRequestFromViewingAppointments(requestId) {
      if (!requestId) {
        return
      }
      this.isNavigatingToRequest = true
      this.tabIndex = 0
      this.openRequestById(requestId)
    }
  }
}
</script>

<style lang="scss" scoped>
.marketing-object-details {
  min-height: 200px;
}

.marketing-details-loading-overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.65);
}

.item {
  cursor: pointer;
}

:deep(.details-row-bg-1) {
  background-color: $color-table-row-2;
}

:deep(.details-row-bg-2) {
  background-color: $color-table-row-1;
}

:deep(label) {
  width: 100%;
}
:deep(.fa-save) {
  color: #fff !important;
}
.nav-button {

  button:not(.sort):not(.page-link) {
    display: none;
  }

  .multiselect, .custom-checkbox {
    pointer-events: none;

    .multiselect__select, .multiselect__placeholder {
      display: none;
    }
  }

  .basic {
    input {
      opacity: 1;
      border: thin solid #ced4da;
      -webkit-box-shadow: inherit;
      box-shadow: inherit;
      margin-bottom: 0;
      pointer-events: initial;
      padding-left: 0.2rem;
      background: none;
    }
  }

  .multi-select-value {
    line-height: 1.5;
  }
}
:deep(textarea.form-control) {
    height: 100px !important;
}
:deep(.tabs .nav-tabs .nav-link a.router-link-active), :deep(.tabs .nav-tabs .nav-link a) {
    color: inherit !important;
    text-decoration: none !important;
  }
  :deep(.customTabs .tabs .nav-tabs .nav-item[role=presentation]) {
    margin-right: 0.2rem;
  }
  :deep(.customTabs .tabs .nav-tabs .nav-item > a.nav-link) {
    margin-right: 0;
    padding: 0;
    box-shadow: none;
    border: 0;
    z-index: -1;
    height: 26.5px;
  }
  :deep(.customTabs .tabs .nav-tabs .nav-item[role=presentation] .nav-link.active > a) {
    font-weight: bold;
    background-color: #fff;
  }
  :deep(.customTabs .tabs .nav-tabs .nav-item[role=presentation] .nav-link:hover > a) {
    font-weight: bold;
    cursor: pointer;
  }
  :deep(.customTabs .tabs .nav-tabs .nav-item[role=presentation] .nav-link > a) {
    color: rgb(0, 68, 95) !important;
    border: 1px solid;
    border-color: #c8ced3 !important;
    border-radius: 0 !important;
    border-bottom: 0 !important;
    margin-right: 0;
    padding: 0.55rem 1.5rem;
    font-weight: 300;
    -webkit-box-shadow: inset 0 16px 20px -15px rgb(238 238 238), 3px 0 2px -2px #ccc;
    box-shadow: inset 0 16px 20px -15px rgb(238 238 238), 3px 0 2px -2px #ccc;
    cursor: default;
    height: 36.25px;
  }
  :deep(.card.shadow) {
    box-shadow: none !important;
  }
  .item-button button.small {
    padding: 0.2rem 0.5rem;
  }
  :deep(.customTabs .tabs .marketing-details-nav) {
    flex-wrap: wrap;
    align-items: flex-start;
    row-gap: 0.5rem;
  }
  :deep(.customTabs .tabs .marketing-details-nav > .nav-item[role=presentation]) {
    flex: 0 0 auto;
  }
  .marketing-details-actions {
    flex: 1 0 auto;
    flex-wrap: wrap;
    align-items: center;
    align-content: flex-start;
    justify-content: flex-end;
    gap: 1rem;
    margin-left: auto;
    padding-left: 10%;
    max-width: 100%;
    min-width: 0;
  }
  :deep(.marketing-details-actions .btn) {
    flex: 1;
    width: auto;
    min-width: 0;
  }
  .tab-content>.active {
    background: #fff;
    position: relative;
  }
  .tab-content .tab-pane {
    padding: 1.25rem;
  }
</style>
